import { useState, useCallback, useEffect, useMemo } from 'react';
import { Popover, Text, Box, Link, Icon } from '@nimbus-ds/components';
import { CloseIcon } from '@nimbus-ds/icons';
import { useNavegate } from 'App/hooks';
import { useStorage } from 'commons/hooks';
import {
  useGetEmailsListQuery,
  usePartialUpdateEmailMutation,
} from 'domains/Configurations/Notifications/notificationsApi';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import usePhysicalLocations from 'domains/Shipping/DeliveryMethods/pages/DeliveryMethodsPage/components/PhysicalLocations/usePhysicalLocations';
import usePickupPoints from 'domains/Shipping/DeliveryMethods/pages/DeliveryMethodsPage/components/PickupPoints/hooks/usePickupPoints';

const MAX_POPOVER_TIMES = 3;

interface PickupEmailPopoverProps {
  children: React.ReactNode;
  isSelected: boolean;
}

export function PickupEmailPopover({
  children,
  isSelected,
}: PickupEmailPopoverProps) {
  const t = useTranslationOrders('savedSearches.popovers.readyToPickup');
  const { goTo } = useNavegate();
  const [isInitialized, setIsInitialized] = useState(false);
  const [popoverTimesShown, setPopoverTimesShown, loadedPopoverTimesShown] =
    useStorage('pickupEmailPopover', 0, true);
  const [isVisible, setIsVisible] = useState(false);
  const [updateEmail] = usePartialUpdateEmailMutation();
  const { data: emails } = useGetEmailsListQuery();
  const { physicalLocations, fetchPhysicalLocations } = usePhysicalLocations();
  const { locationsAsPickupPoint, otherPickupPoints, fetchPickupPoints } =
    usePickupPoints();
  const pickupEmail = useMemo(
    () => emails?.orders.find((email) => email.type === 'orderreadyforpickup'),
    [emails],
  );
  const pickupPoints = useMemo(
    () => [...locationsAsPickupPoint, ...otherPickupPoints],
    [locationsAsPickupPoint, otherPickupPoints],
  );

  useEffect(fetchPhysicalLocations, [fetchPhysicalLocations]);
  useEffect(() => {
    fetchPickupPoints();
  }, [fetchPickupPoints]);

  useEffect(() => {
    if (
      !isInitialized &&
      pickupEmail &&
      !pickupEmail.active &&
      loadedPopoverTimesShown &&
      popoverTimesShown < MAX_POPOVER_TIMES
    ) {
      setIsInitialized(true);
      setIsVisible(true);
    }
  }, [
    isSelected,
    loadedPopoverTimesShown,
    popoverTimesShown,
    isInitialized,
    pickupEmail,
  ]);

  const onRedirect = useCallback(() => {
    if (pickupEmail) {
      // Enable the email and redirect to notifications settings
      updateEmail({
        id: pickupEmail.id.toString(),
        payload: {
          data: {
            active: true,
          },
        },
      })
        .unwrap()
        .then(() => goTo(`/settings/notifications/${pickupEmail.id}`));
    }
  }, [goTo, pickupEmail, updateEmail]);

  const handleRedirect = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRedirect();
  };

  const handleClose = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setPopoverTimesShown(MAX_POPOVER_TIMES); // Set to max to prevent showing again
    setIsVisible(false);
  };

  const handleVisibilityChange = (visible: boolean) => {
    if (!visible) {
      setIsVisible(false);
      if (loadedPopoverTimesShown && popoverTimesShown < MAX_POPOVER_TIMES) {
        setPopoverTimesShown(popoverTimesShown + 1);
      }
    } else {
      setIsVisible(true);
    }
  };

  // Basically, just show the popover warning description
  // if there are physical locations but no pickup points
  const popoverDescriptionKey =
    physicalLocations?.length && pickupPoints && !pickupPoints.length
      ? 'description-warning'
      : 'description';

  // Don't show the popover if missing the email or loading locations / pickup points
  if (!pickupEmail || !physicalLocations || !pickupPoints) {
    return <div>{children}</div>;
  }

  return (
    <Popover
      position="bottom-start"
      visible={isVisible}
      onVisibility={handleVisibilityChange}
      content={
        <Box
          width="280px"
          backgroundColor="transparent"
          gap="1"
          display="flex"
          flexDirection="column"
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Text fontWeight="bold">{t('title')}</Text>
            <Icon
              source={<CloseIcon />}
              color="neutral-textHigh"
              onClick={handleClose}
              style={{ cursor: 'pointer' }}
            />
          </Box>
          <Box flexDirection="column">
            <Text color="neutral-textDisabled" fontSize="caption">
              <span
                dangerouslySetInnerHTML={{ __html: t(popoverDescriptionKey) }}
              />
            </Text>
            <Link
              as="button"
              onClick={handleRedirect}
              appearance="primary"
              fontSize="caption"
              textDecoration="none"
            >
              {t('email')}
            </Link>
          </Box>
        </Box>
      }
    >
      {children}
    </Popover>
  );
}
